import { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { CurrencySymbolEnum } from '../../lib/enums/currency.enum';
import lang from '../../lib/language';
import { TopGainersAndLosersPriceOption } from '../../lib/models/fundamental/types';

import iconSort from '../../assets/img/sort.png';
import ClickableDiv from '../ClickableDiv/ClickableDiv';
import useIsMobile from '../hooks/useIsMobile';
import MobileDrawer from '../MobileDrawer/MobileDrawer';

import { FILTER_RANGE_OPTIONS } from './FilterDropDownOptions';

import styles from './FilterDropDown.module.scss';

type Props = {
  handleFilterOption: (option: number[], optionValue: string) => void;
  optionValue?: string;
  isDisabled?: boolean;
  isDropdownOpen?: boolean;
  currencySymbol?: CurrencySymbolEnum;
}

const FilterDropDown = (compProps: Props) => {
  const {
    handleFilterOption,
    optionValue,
    isDisabled,
    currencySymbol = CurrencySymbolEnum.USD,
  } = compProps;

  const toDisplayLabel = (option: string) =>
    (currencySymbol === CurrencySymbolEnum.USD
      ? option
      : option.replace(/\$/g, currencySymbol));
  const { isMobile } = useIsMobile();
  const [value, setValue] = useState<string>(optionValue || TopGainersAndLosersPriceOption.fifteenToFifty);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isFilterModal, setFilterModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    !optionValue && setValue(TopGainersAndLosersPriceOption.fifteenToFifty);
  }, [optionValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const handleToggleDropdown = () => {
    if (!isDisabled) {
      setIsDropdownOpen(!isDropdownOpen);
    }
  };

  const handleOptionClick = (option: string) => {
    const indexOption = Object.values(TopGainersAndLosersPriceOption).indexOf(option);
    handleFilterOption(FILTER_RANGE_OPTIONS[indexOption], option);
    setValue(option);
    setIsDropdownOpen(false);
  };

  return (
    <ClickableDiv
      ref={dropdownRef}
      onClick={handleToggleDropdown}
      className={classNames(
        styles.wrapper,
        {
          [styles.open]: isDropdownOpen,
        },
      )}
    >
      {!isMobile && (
        <div
          className={styles.iconWrapper}
        >
          <img src={iconSort} className={styles.icon} alt="sort" />
        </div>
      )}

      {!isMobile && (
        <>
          <div
            className={classNames(
              styles.desktopDropdownTrigger,
              {
                [styles.disabled]: isDisabled,
              },
            )}
          >
            {toDisplayLabel(value)}
          </div>

          {isDropdownOpen && (
            <div
              className={styles.desktopDropdownMenu}
            >
              {Object.values(TopGainersAndLosersPriceOption).map((option) => (
                <ClickableDiv
                  key={option}
                  className={classNames(
                    styles.desktopDropdownOption,
                    { [styles.selected]: option === value },
                  )}
                  onClick={() => handleOptionClick(option)}
                >
                  {toDisplayLabel(option)}
                </ClickableDiv>
              ))}
            </div>
          )}
        </>
      )}

      {isMobile && (
      <>
        <ClickableDiv
          disabled={isDisabled}
          className={styles.mobFilterButton}
          onClick={() => setFilterModal(true)}
        >
          <img src={iconSort} alt="icon sort" className={styles.mobFilterButtonIcon} />
          <span className={styles.mobFilterButtonText}>{toDisplayLabel(value)}</span>
        </ClickableDiv>
        <MobileDrawer
          isOpen={isFilterModal}
          title={lang.mobileSortButton()}
          onClose={() => setFilterModal(false)}
        >
          <div className={styles.rangeButtonWrapper}>
            {
                Object.values(TopGainersAndLosersPriceOption).map((option) => (
                  <ClickableDiv
                    className={styles.rangeButton}
                    key={option}
                    onClick={() => {
                      const indexOption = Object.values(TopGainersAndLosersPriceOption).indexOf(option);
                      handleFilterOption(FILTER_RANGE_OPTIONS[indexOption], option);
                      setValue(option);
                      setFilterModal(false);
                    }}
                  >
                    <span className={option === value ? styles.rangeButtonTextActive : styles.rangeButtonText}>
                      {toDisplayLabel(option)}
                    </span>
                  </ClickableDiv>
                ))
              }
          </div>
        </MobileDrawer>
      </>
      )}
    </ClickableDiv>
  );
};

export default FilterDropDown;
